import { Clock } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import { useSession } from '@/hooks/useSession';
import { useSubscription } from '@/hooks/useSettings';
import { cn } from '@/lib/utils';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Trial countdown, then the lapsed notice, across the top of every signed-in
 * screen. There is no free plan: once the trial ends the server refuses writes
 * (402 PLAN_LAPSED), so this is the one place that says why and what to do.
 */
export function PlanBanner() {
  const { pathname } = useLocation();
  const { isAdmin, team } = useSession();
  const { data } = useSubscription();

  // A demo team never subscribes; the trial countdown is noise there.
  if (team?.isDemo) return null;
  if (!data || pathname === '/subscription') return null;
  if (data.tier !== 'trial' && data.tier !== 'expired') return null;

  const lapsed = data.tier === 'expired';
  const daysLeft = data.trialEndsAt
    ? Math.max(1, Math.ceil((Date.parse(data.trialEndsAt) - Date.now()) / DAY_MS))
    : 0;

  return (
    <div
      className={cn(
        'border-b border-[var(--line)]',
        lapsed ? 'bg-[var(--accent-soft)]' : 'bg-[var(--surface-2)]'
      )}
    >
      <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-2 px-6 py-3">
        <Clock className="size-4 shrink-0 text-[var(--accent)]" aria-hidden />
        <p className="min-w-0 flex-1 text-sm">
          <span className="font-medium">
            {lapsed
              ? 'Your trial has ended'
              : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left in your trial`}
          </span>
          <span className="text-muted">
            {lapsed
              ? isAdmin
                ? '. Everything is still here to view and export. Pick a plan to keep working.'
                : '. Everything is still here to view. Ask an admin to pick a plan.'
              : '. Plans start at $19 a month.'}
          </span>
        </p>
        {isAdmin ? (
          <Button asChild variant={lapsed ? 'primary' : 'secondary'} size="sm" className="shrink-0">
            <Link to="/subscription">Choose a plan</Link>
          </Button>
        ) : null}
      </div>
    </div>
  );
}
